import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableRow,
  cn
} from '@repo/ui'
import { EllipsisVertical, Eye, KeyRound, Pencil, Plus, RefreshCw, Settings, StopCircle, Trash } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import type { Connector } from '@/api/content/connector'
import {
  ConnectorState,
  ConnectorType,
  cancelSyncConnector,
  connectorLabel,
  connectorStateLabel,
  deleteConnector,
  reauthConnector,
  resyncConnector
} from '@/api/content/connector'
import type { UserOrgProfile } from '@/api/user/profile'
import { AddConnectorDialog, ConnectorIcon } from './AddConnectorDialog'
import { ConnectorDescription } from './ConnectorDescription'
import { ConnectorFileTree } from './ConnectorFileTree'
import { PageContent } from './PageContent'
import { PageHeader } from './PageHeader'
import type { BreadcrumbEntry } from './PageHeader'
import type { ReactNode } from 'react'

function ContentBoundary({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  return embedded ? children : <PageContent>{children}</PageContent>
}

function formatLastSyncedAt(tsMs: number | null | undefined): string {
  if (tsMs == null || tsMs === 0) return 'Never'
  return new Date(tsMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function canReauthenticate(connector: Connector): boolean {
  return connector.connectorType !== ConnectorType.WebCrawler && connector.state !== ConnectorState.Deleting
}

function canResync(connector: Connector): boolean {
  const nonResyncableStates: ReadonlyArray<string> = [
    ConnectorState.Syncing,
    ConnectorState.Deleting,
    ConnectorState.Incomplete,
    ConnectorState.Authorised
  ]
  return !nonResyncableStates.includes(connector.state)
}

function canCancelSync(connector: Connector): boolean {
  return connector.state === ConnectorState.Syncing
}

function canEditConnector(connector: Connector, isOwner: boolean): boolean {
  if (!isOwner) return false
  const nonEditableStates: ReadonlyArray<string> = [
    ConnectorState.Incomplete,
    ConnectorState.Syncing,
    ConnectorState.Deleting
  ]
  return !nonEditableStates.includes(connector.state)
}

function canEditDescription(connector: Connector): boolean {
  return connector.state !== ConnectorState.Deleting
}

function canDelete(connector: Connector): boolean {
  return connector.state !== ConnectorState.Deleting
}

async function handleReauthRedirect(
  connectorId: number,
  projectId: number | undefined,
  beforeOAuthRedirect?: () => void
): Promise<string | null> {
  try {
    const redirectUrl = await reauthConnector(connectorId, projectId)
    const parsed = new URL(redirectUrl)
    if (parsed.protocol !== 'https:') {
      throw new Error('Redirect URL must use HTTPS')
    }
    beforeOAuthRedirect?.()
    window.location.href = redirectUrl
    return null
  } catch (err) {
    Sentry.captureException(err, { extra: { connectorId, projectId } })
    console.error('Re-authentication failed:', err)
    return err instanceof Error ? err.message : 'Re-authentication failed'
  }
}

interface ConnectorPageProps {
  breadcrumbs: Array<BreadcrumbEntry>
  connectors: Array<Connector>
  projectId?: number
  userOrgProfile: UserOrgProfile
  initialConnectorId?: number | undefined
  onClearConnectorId?: (() => void) | undefined
  embedded?: boolean
  onAuthorityChanged?: () => void | Promise<void>
  beforeOAuthRedirect?: () => void
}

export function ConnectorPage({
  breadcrumbs,
  connectors,
  projectId,
  userOrgProfile,
  initialConnectorId,
  onClearConnectorId,
  embedded = false,
  onAuthorityChanged,
  beforeOAuthRedirect
}: ConnectorPageProps) {
  const router = useRouter()
  const refreshAuthority = useCallback(async () => {
    if (onAuthorityChanged !== undefined) await onAuthorityChanged()
    else await router.invalidate()
  }, [onAuthorityChanged, router])
  const hasTransientConnectors = connectors.some(
    (c) => c.state === ConnectorState.Syncing || c.state === ConnectorState.Deleting
  )

  useEffect(() => {
    if (!hasTransientConnectors) return
    const id = setInterval(() => {
      void refreshAuthority()
    }, 10_000)
    return () => clearInterval(id)
  }, [hasTransientConnectors, refreshAuthority])

  const displayedConnectors = initialConnectorId ? connectors.filter((c) => c.id === initialConnectorId) : connectors

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [deleteItem, setDeleteItem] = useState<Connector | null>(null)
  const [viewItem, setViewItem] = useState<Connector | null>(null)
  const [editItem, setEditItem] = useState<Connector | null>(null)
  const [viewDescriptionItem, setViewDescriptionItem] = useState<Connector | null>(null)
  const [editDescriptionItem, setEditDescriptionItem] = useState<Connector | null>(null)

  return (
    <>
      {!embedded && <PageHeader breadcrumbs={breadcrumbs} />}
      <ContentBoundary embedded={embedded}>
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {connectors.length === 0
                ? 'No connectors yet.'
                : `${displayedConnectors.length} connector${displayedConnectors.length === 1 ? '' : 's'}`}
            </p>
            <Button size="sm" onClick={() => setShowAddDialog(true)}>
              <Plus className="size-4" />
              Add
            </Button>
          </div>

          {onClearConnectorId && (
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
              <span className="text-muted-foreground">Showing a single connector.</span>
              <button
                type="button"
                onClick={onClearConnectorId}
                className="font-medium underline hover:text-foreground"
              >
                View all
              </button>
            </div>
          )}

          {displayedConnectors.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableBody>
                  {displayedConnectors.map((item) => (
                    <ConnectorRow
                      key={item.id}
                      item={item}
                      isOwner={userOrgProfile.uid === item.createdById}
                      resyncEnabled={userOrgProfile.enabled_features.connector_resync}
                      projectId={projectId}
                      onView={() => setViewItem(item)}
                      onEdit={() => {
                        setViewItem(item)
                        setEditItem(item)
                      }}
                      onEditDescription={() => {
                        setViewDescriptionItem(item)
                        setEditDescriptionItem(item)
                      }}
                      onDelete={() => setDeleteItem(item)}
                      onAuthorityChanged={refreshAuthority}
                      {...(beforeOAuthRedirect !== undefined ? { beforeOAuthRedirect } : {})}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </ContentBoundary>

      {viewItem && (
        <ConnectorConfigDialog
          item={viewItem}
          editMode={!!editItem}
          {...(projectId !== undefined ? { projectId } : {})}
          onCancelEdit={() => {
            setViewItem(null)
            setEditItem(null)
          }}
          onClose={() => {
            setViewItem(null)
            setEditItem(null)
          }}
          onAuthorityChanged={refreshAuthority}
        />
      )}

      {viewDescriptionItem && (
        <ConnectorEditDescriptionDialog
          item={viewDescriptionItem}
          editMode={!!editDescriptionItem}
          {...(projectId !== undefined ? { projectId } : {})}
          onEdit={() => setEditDescriptionItem(viewDescriptionItem)}
          onCancelEdit={() => {
            setViewDescriptionItem(null)
            setEditDescriptionItem(null)
          }}
          onClose={() => {
            setViewDescriptionItem(null)
            setEditDescriptionItem(null)
          }}
          onAuthorityChanged={refreshAuthority}
        />
      )}

      <AddConnectorDialog
        open={showAddDialog}
        userOrgProfile={userOrgProfile}
        {...(projectId !== undefined ? { projectId } : {})}
        onClose={() => setShowAddDialog(false)}
        onAdded={refreshAuthority}
        {...(beforeOAuthRedirect !== undefined ? { beforeOAuthRedirect } : {})}
      />
      <DeleteConnectorDialog
        item={deleteItem}
        {...(projectId !== undefined ? { projectId } : {})}
        onClose={() => setDeleteItem(null)}
        onDeleted={refreshAuthority}
      />
    </>
  )
}

function ConnectorRow({
  item,
  isOwner,
  resyncEnabled,
  projectId,
  onView,
  onEdit,
  onEditDescription,
  onDelete,
  onAuthorityChanged,
  beforeOAuthRedirect
}: {
  item: Connector
  isOwner: boolean
  resyncEnabled: boolean
  projectId: number | undefined
  onView: () => void
  onEdit: () => void
  onEditDescription: () => void
  onDelete: () => void
  onAuthorityChanged: () => void | Promise<void>
  beforeOAuthRedirect?: () => void
}) {
  const [isReauthing, setIsReauthing] = useState(false)
  const [reauthError, setReauthError] = useState<string | null>(null)
  const [isResyncing, setIsResyncing] = useState(false)
  const [resyncError, setResyncError] = useState<string | null>(null)
  const [isCancellingSync, setIsCancellingSync] = useState(false)
  const [cancelSyncError, setCancelSyncError] = useState<string | null>(null)
  const stateInfo = connectorStateLabel(item.state, item.statusDetails)
  const updatedByName = `${item.updatedByFirstName} ${item.updatedBySurname}`.trim()

  async function handleReauth() {
    setIsReauthing(true)
    setReauthError(null)
    const error = await handleReauthRedirect(item.id, projectId, beforeOAuthRedirect)
    if (error) setReauthError(error)
    setIsReauthing(false)
  }

  async function handleResync() {
    setIsResyncing(true)
    setResyncError(null)
    try {
      await resyncConnector(item.id, projectId)
      await onAuthorityChanged()
    } catch (err) {
      Sentry.captureException(err, { extra: { connectorId: item.id, projectId } })
      setResyncError(err instanceof Error ? err.message : 'Resync failed')
    } finally {
      setIsResyncing(false)
    }
  }

  async function handleCancelSync() {
    setIsCancellingSync(true)
    setCancelSyncError(null)
    try {
      await cancelSyncConnector(item.id, projectId)
      await onAuthorityChanged()
    } catch (err) {
      Sentry.captureException(err, { extra: { connectorId: item.id, projectId } })
      setCancelSyncError(err instanceof Error ? err.message : 'Cancel sync failed')
    } finally {
      setIsCancellingSync(false)
    }
  }

  return (
    <TableRow className="cursor-pointer hover:bg-muted/50 [&_td]:p-4 [&_td]:align-top" onClick={onView}>
      <TableCell className="w-10 align-middle">
        <ConnectorIcon type={item.connectorType} className="size-4" />
      </TableCell>
      <TableCell className="w-full">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-sm font-medium">{connectorLabel(item.connectorType)}</span>
          <span className={cn('text-xs font-medium', stateInfo.colorClass)}>{stateInfo.label}</span>
          {stateInfo.message && <span className="text-xs text-muted-foreground">{stateInfo.message}</span>}
        </div>
        <div className="mb-1 line-clamp-4 text-xs wrap-anywhere whitespace-normal">{item.connectorDescription}</div>
        <div className="text-xs text-muted-foreground">Last synced: {formatLastSyncedAt(item.lastSyncedAtTsMs)}</div>
        {reauthError && (
          <div role="alert" className="mt-1 text-xs text-destructive">
            {reauthError}
          </div>
        )}
        {resyncError && (
          <div role="alert" className="mt-1 text-xs text-destructive">
            {resyncError}
          </div>
        )}
        {cancelSyncError && (
          <div role="alert" className="mt-1 text-xs text-destructive">
            {cancelSyncError}
          </div>
        )}
      </TableCell>
      <TableCell className="w-10 align-middle whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">{updatedByName}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" aria-label="Connector actions">
                <EllipsisVertical className="size-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onView()
                }}
              >
                <Eye className="size-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!canEditDescription(item)}
                onClick={(e) => {
                  e.stopPropagation()
                  onEditDescription()
                }}
              >
                <Pencil className="size-4" />
                Edit Description
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!canEditConnector(item, isOwner)}
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit()
                }}
              >
                <Settings className="size-4" />
                Configure
              </DropdownMenuItem>
              {isOwner && canReauthenticate(item) && (
                <DropdownMenuItem
                  disabled={isReauthing}
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleReauth()
                  }}
                >
                  <KeyRound className="size-4" />
                  Re-authenticate
                </DropdownMenuItem>
              )}
              {resyncEnabled && (
                <DropdownMenuItem
                  disabled={isResyncing || !canResync(item)}
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleResync()
                  }}
                >
                  <RefreshCw className="size-4" />
                  Resync
                </DropdownMenuItem>
              )}
              {resyncEnabled && (
                <DropdownMenuItem
                  disabled={isCancellingSync || !canCancelSync(item)}
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleCancelSync()
                  }}
                >
                  <StopCircle className="size-4" />
                  Cancel sync
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={!canDelete(item)}
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
              >
                <Trash className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  )
}

function ConnectorConfigDialog({
  item,
  editMode,
  projectId,
  onCancelEdit,
  onClose,
  onAuthorityChanged
}: {
  item: Connector
  editMode: boolean
  projectId?: number
  onCancelEdit: () => void
  onClose: () => void
  onAuthorityChanged: () => void | Promise<void>
}) {
  const stateInfo = connectorStateLabel(item.state, item.statusDetails)
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-4xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <ConnectorIcon type={item.connectorType} className="size-5" />
            <div>
              <div className="flex items-center gap-2">
                <DialogTitle>{connectorLabel(item.connectorType)}</DialogTitle>
                <span className={cn('text-xs font-medium', stateInfo.colorClass)}>{stateInfo.label}</span>
              </div>
              <DialogDescription className="sr-only">Connector configuration</DialogDescription>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Last synced: {formatLastSyncedAt(item.lastSyncedAtTsMs)}</span>
                <span>·</span>
                <span className="font-medium">{`${item.updatedByFirstName} ${item.updatedBySurname}`.trim()}</span>
              </div>
            </div>
          </div>
        </DialogHeader>
        <ConnectorFileTree
          {...(projectId !== undefined ? { projectId } : {})}
          connectorId={item.id}
          connectorType={item.connectorType}
          editMode={editMode}
          onCancelEdit={onCancelEdit}
          onAuthorityChanged={onAuthorityChanged}
        />
        {!editMode && (
          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

function DeleteConnectorDialog({
  item,
  projectId,
  onClose,
  onDeleted
}: {
  item: Connector | null
  projectId?: number
  onClose: () => void
  onDeleted: () => void | Promise<void>
}) {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    if (!item) return
    setIsPending(true)
    setError(null)
    try {
      await deleteConnector(item.id, projectId)
      await onDeleted()
      onClose()
    } catch (err) {
      Sentry.captureException(err, { extra: { connectorId: item.id, projectId } })
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Dialog
      open={!!item}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Connector</DialogTitle>
          <DialogDescription className="sr-only">Delete Connector Modal</DialogDescription>
        </DialogHeader>
        <p className="text-sm">
          Are you sure you want to delete this content connector? This will also delete all data associated with it from
          Snowvault. This action cannot be undone. Cleanup runs in the background; the connector will remain visible as
          &ldquo;Deleting&rdquo; until it completes.
        </p>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={handleConfirm} disabled={isPending}>
            {isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ConnectorEditDescriptionDialog({
  item,
  editMode,
  onEdit,
  onCancelEdit,
  onClose,
  onAuthorityChanged
}: {
  item: Connector
  editMode: boolean
  onEdit: () => void
  onCancelEdit: () => void
  onClose: () => void
  onAuthorityChanged: () => void | Promise<void>
}) {
  const stateInfo = connectorStateLabel(item.state, item.statusDetails)
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-4xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <ConnectorIcon type={item.connectorType} className="size-5" />
            <div>
              <div className="flex items-center gap-2">
                <DialogTitle>{connectorLabel(item.connectorType)}</DialogTitle>
                <span className={cn('text-xs font-medium', stateInfo.colorClass)}>{stateInfo.label}</span>
              </div>
              <DialogDescription className="sr-only">Connector description</DialogDescription>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Last synced: {formatLastSyncedAt(item.lastSyncedAtTsMs)}</span>
                <span>·</span>
                <span className="font-medium">{`${item.updatedByFirstName} ${item.updatedBySurname}`.trim()}</span>
              </div>
            </div>
          </div>
        </DialogHeader>
        <ConnectorDescription
          connectorDescription={item.connectorDescription}
          connectorId={item.id}
          editMode={editMode}
          onClose={onClose}
          onCancelEdit={onCancelEdit}
          onSaved={onAuthorityChanged}
        />
        {!editMode && (
          <DialogFooter>
            <Button variant="outline" onClick={onEdit}>
              <Pencil className="size-4" />
              Edit
            </Button>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
